<template>
  <div class="pagination-container">
    <div
      v-if="totalRows > perPage"
      class="btn-group pagination"
      role="group"
      aria-label="Pagination"
      :class="totalRows <= perPage ? 'disabled' : ''">
      <button
        type="button"
        :class="['btn', 'btn-sm', 'btn-default', btnSize]"
        :disabled="currentPage == 1"
        @click.prevent="currentPage == 1 ? _return : currentPage--">
        <span aria-hidden="true">&laquo;</span>
      </button>

      <button
        v-show="showPrev"
        type="button"
        class="first-page"
        :class="['btn', 'btn-sm', 'btn-default', btnSize, currentPage == 1 ? 'active' : '']"
        @click.prevent="currentPage = 1">
        1
      </button>

      <span
        v-show="showPrev"
        :class="['btn', 'btn-sm', 'btn-default', btnSize]"
        >...</span
      >

      <button
        v-for="(item, index) in pageLinks"
        :key="index"
        type="button"
        :class="[
          'btn',
          'btn-sm',
          btnSize,
          btnVariant(index),
          index + diff == currentPage ? 'selected' : '',
          index + diff != currentPage ? 'hidden-xs-down' : '',
        ]"
        @click.prevent="currentPage = index + diff">
        {{ index + diff }}
      </button>

      <span
        v-show="showNext"
        :class="['btn', 'btn-sm', 'btn-default', btnSize]"
        >...</span
      >

      <button
        v-show="showNext"
        type="button"
        class="last-page"
        :class="['btn', 'btn-sm', 'btn-default', btnSize, numberOfPages == currentPage ? 'active' : '']"
        @click.prevent="currentPage = numberOfPages">
        {{ numberOfPages }}
      </button>

      <button
        type="button"
        :class="['btn', 'btn-sm', 'btn-default', btnSize]"
        :disabled="currentPage == numberOfPages"
        @click.prevent="currentPage == numberOfPages ? _return : currentPage++">
        <span aria-hidden="true">&raquo;</span>
      </button>
    </div>
    <div
      v-if="perPage < totalRows"
      class="pagination-row-count badge badge-disabled">
      <span v-show="from != to">{{ from }} – {{ to }} {{ $t('prepositions.of') }} {{ totalRows }} </span>
      <span v-show="from == to">{{ from }} {{ $t('prepositions.of') }} {{ totalRows }} </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';

  interface Props {
    limit?: number;
    perPage?: number;
    totalRows?: number;
    size?: string;
    variant?: string;
  }
  const props = withDefaults(defineProps<Props>(), {
    limit: 7,
    perPage: 20,
    totalRows: 20,
    size: 'default',
    variant: '',
  });
  const currentPage = defineModel<number>({ default: 1 });

  const diff = ref(1);
  const showPrev = ref(false);
  const showNext = ref(false);

  const numberOfPages = computed(() => {
    const result = Math.ceil(props.totalRows / props.perPage);
    return Math.max(1, result);
  });
  const btnSize = computed(() => {
    return !props.size || props.size === 'default' ? '' : `btn-${props.size}`;
  });
  const pageLinks = computed(() => {
    let result = props.limit;

    // if less pages than limit just show this pages
    if (numberOfPages.value <= props.limit) {
      return numberOfPages.value;
    }
    // if at the beggining of the list or at the end show full number of pages within limit - 2
    // -2 is reserves space for two buttons: "..." and "first/last button"
    if (currentPage.value <= props.limit - 2) {
      result = props.limit - 2;
    }
    // at the end of the range
    if (currentPage.value > numberOfPages.value - props.limit + 2) {
      result = props.limit - 2;
    }
    // if somehere in the middle show just limit - 4 links in the middle and one button
    // on the left with "..." and on button on the right preceeded with "..."
    if (currentPage.value >= props.limit - 2 && currentPage.value <= numberOfPages.value - props.limit + 2) {
      result = props.limit - 4;
    }
    return result;
  });
  const from = computed(() => {
    return (currentPage.value - 1) * props.perPage + 1;
  });
  const to = computed(() => {
    return Math.min(props.totalRows, from.value + (props.perPage - 1));
  });

  function btnVariant(index: number) {
    return index + diff.value === currentPage.value ? `btn-${props.variant}` : 'btn-default';
  }
  function updateButtonsState() {
    diff.value = 1;
    showPrev.value = false;
    showNext.value = false;
    if (numberOfPages.value <= props.limit) {
      return;
    }

    if (currentPage.value > numberOfPages.value) {
      currentPage.value = 1;
    }
    if (currentPage.value <= props.limit - 2) {
      diff.value = 1;
      showNext.value = true;
    }
    if (currentPage.value > numberOfPages.value - props.limit + 2) {
      diff.value = numberOfPages.value - props.limit + 3;
      showPrev.value = true;
    }
    if (currentPage.value >= props.limit - 2 && currentPage.value <= numberOfPages.value - props.limit + 2) {
      diff.value = currentPage.value - 1;
      showPrev.value = true;
      showNext.value = true;
    }
  }

  watch(currentPage, updateButtonsState);
  watch(numberOfPages, updateButtonsState);
  watch(() => props.limit, updateButtonsState);
  onMounted(updateButtonsState);
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables' as *;

  #app {
    .pagination-container {
      display: flex;
      flex-basis: 100%;
      align-items: center;

      .pagination {
        flex: 0 1 auto;

        .btn {
          border: solid 0.1rem var(--defaultButtonColorLighter);

          font-weight: 400;

          &.active {
            border: solid 0.1rem var(--actionsColor);
            background: var(--actionsColor);
            color: var(--actionsColorReverse);
          }
        }

        span.btn {
          &:first-of-type,
          &:last-of-type {
            border-radius: 0;
          }
        }

        &.disabled {
          .btn {
            display: none;
          }
        }

        margin-right: 1.6rem;
      }

      .pagination-row-count {
        flex: 0 1 auto;
        padding: 1rem 1.6rem;
      }
    }
  }
</style>
